import { apiRequestWithHeaders } from "@/services/api/client";

/**
 * Downloads a file from the files endpoint with authentication.
 * Uses the authenticated API client to fetch the file as a blob,
 * then triggers a browser download.
 *
 * @param fileId - The UUID of the file to download
 * @param filename - Optional filename for the downloaded file (defaults to fileId.pdf)
 */
export const downloadFile = async (fileId: string, filename?: string): Promise<void> => {
  try {
    const response = await apiRequestWithHeaders<Blob>({
      method: "GET",
      url: `/files/${fileId}`,
      responseType: "blob",
      headers: {
        Accept: "application/octet-stream",
      },
    });

    let finalFilename = filename || `${fileId}.pdf`;

    if (!filename) {
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"] ||
        response.headers["CONTENT-DISPOSITION"];

      if (contentDisposition) {
        let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=['"]([^'"]+)['"]/i);
        }
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;,\s]+)/i);
        }

        if (filenameMatch && filenameMatch[1]) {
          finalFilename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, "");
        }
      }
    }

    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = finalFilename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/**
 * @deprecated Use downloadFile instead. This alias exists for backward compatibility.
 */
export const downloadAdminFile = downloadFile;

/**
 * Downloads a document from the documents endpoint with authentication.
 * Uses the authenticated API client to fetch the file as a blob,
 * then triggers a browser download.
 *
 * @param fileId - The UUID of the document to download
 * @param filename - Optional filename for the downloaded file
 */
export const downloadDocument = async (fileId: string, filename?: string): Promise<void> => {
  try {
    const response = await apiRequestWithHeaders<Blob>({
      method: "GET",
      url: `/documents/download/${fileId}`,
      responseType: "blob",
      headers: {
        'Accept': 'application/octet-stream',
      },
    });

    // Use provided filename or extract from Content-Disposition header
    let finalFilename = filename || fileId; // Use provided filename or fallback to fileId

    // If no filename was provided, try to extract from Content-Disposition header
    if (!filename) {
      const contentDisposition = response.headers['content-disposition'] ||
        response.headers['Content-Disposition'] ||
        response.headers['CONTENT-DISPOSITION'];

      if (contentDisposition) {
        // Try multiple patterns to extract filename
        let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=['"]([^'"]+)['"]/i);
        }
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;,\s]+)/i);
        }

        if (filenameMatch && filenameMatch[1]) {
          finalFilename = filenameMatch[1].trim();
          // Remove quotes if present
          finalFilename = finalFilename.replace(/^['"]|['"]$/g, '');
        }
      }
    }

    // Create blob URL and trigger download
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalFilename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
}; 